import { createContext, useContext } from 'react';

/**
 * The toast API and the hook that reaches it.
 *
 * Split from the provider that renders them, matching how `AuthProvider` and
 * `useAuth` are arranged: a module exporting both a component and a hook
 * defeats React Fast Refresh, which then reloads the whole page on every edit.
 */

export type ToastTone = 'success' | 'error' | 'info';

export interface Toast {
  id: number;
  tone: ToastTone;
  message: string;
  /** A second line - which record, or why it failed. */
  detail?: string;
  /** A single follow-up, typically "Undo". */
  action?: { label: string; onClick: () => void };
}

export interface ToastOptions {
  detail?: string;
  action?: { label: string; onClick: () => void };
  /** Milliseconds on screen. An error defaults to twice as long. */
  duration?: number;
}

export interface ToastApi {
  show: (tone: ToastTone, message: string, options?: ToastOptions) => void;
  success: (message: string, options?: ToastOptions) => void;
  error: (message: string, options?: ToastOptions) => void;
  info: (message: string, options?: ToastOptions) => void;
  dismiss: (id: number) => void;
}

export const ToastContext = createContext<ToastApi | null>(null);

/**
 * Toasts raised with no provider above them are silently dropped.
 *
 * Every screen is rendered in isolation somewhere - a test, a preview, an
 * embedded panel - and a missing provider should not be the thing that takes
 * it down. The cost is a swallowed notification in those contexts, which is
 * the right trade.
 */
const SILENT: ToastApi = {
  show: () => {},
  success: () => {},
  error: () => {},
  info: () => {},
  dismiss: () => {},
};

export function useToast(): ToastApi {
  return useContext(ToastContext) ?? SILENT;
}
